<script lang="ts" setup>
  import { type ButtonHTMLAttributes, type Component, computed, ref, type StyleValue, useSlots } from 'vue'
  import Button, { type ButtonPassThroughOptions, type ButtonProps } from 'primevue/button'
  import { omit } from 'lodash-es'
  import type { PassThroughOptions } from 'primevue/passthrough'
  import { ToIcon } from '@/plugins/IconPlugin.ts'
  import SpinnerDefault from '@/elements/SpinnerDefault.vue'

  //import * as AllConfiguredIcons from '@/config/icons'

  export interface CustomButtonProps extends /* @vue-ignore */ ButtonHTMLAttributes {
    /**
     * Inline style of the button.
     */
    style?: StyleValue
    /**
     * Style class of the button.
     */
    class?: CssClassType
    /**
     * Text of the button.
     */
    label?: string | undefined
    /**
     * Name of the icon.
     */
    icon?: AllIconsType | Component | null

    /**
     * Position of the icon.
     * @defaultValue left
     */
    iconPos?: 'left' | 'right' | 'top' | 'bottom' | undefined
    /**
     * Style class of the icon.
     */
    iconClass?: string | undefined
    /**
     * Value of the badge.
     */
    badge?: string | undefined
    /**
     * Style class of the badge.
     */
    badgeClass?: string | undefined
    /**
     * Severity type of the badge.
     */
    badgeSeverity?: HintedString<'secondary' | 'info' | 'success' | 'warning' | 'danger' | 'contrast'> | null | undefined
    /**
     * Whether the button is in loading state.
     * @defaultValue false
     */
    loading?: boolean | undefined
    /**
     * Icon to display in loading state.
     */
    loadingIcon?: string | undefined
    /**
     *  Add a link style to the button.
     * @defaultValue false
     */
    link?: boolean | undefined
    /**
     * Defines the style of the button.
     */
    severity?: HintedString<'secondary' | 'success' | 'info' | 'warn' | 'help' | 'danger' | 'contrast'> | undefined
    /**
     * Add a shadow to indicate elevation.
     * @defaultValue false
     */
    raised?: boolean | undefined
    /**
     * Add a circular border radius to the button.
     * @defaultValue false
     */
    rounded?: boolean | undefined
    /**
     * Add a textual class to the button without a background initially.
     * @defaultValue false
     */
    text?: boolean | undefined
    /**
     * Add a border class without a background initially.
     * @defaultValue false
     */
    outlined?: boolean | undefined
    /**
     * Defines the size of the button.
     */

    size?: 'extra-small' | 'small' | 'large' | undefined
    /**
     * Add a plain textual class to the button without a background initially.
     * @defaultValue false
     */
    plain?: boolean | undefined
    /**
     * Used to pass attributes to DOM elements inside the component.
     * @type {ButtonPassThroughOptions}
     */
    pt?: PassThrough<ButtonPassThroughOptions>
    /**
     * Used to configure passthrough(pt) options of the component.
     * @type {PassThroughOptions}
     */
    ptOptions?: PassThroughOptions
    /**
     * When enabled, it removes component related styles in the core.
     * @defaultValue false
     */
    unstyled?: boolean

    fluid?: boolean

    variant?: 'outlined' | 'text' | 'link'

    /**
     * Async function to execute on click. Button will show loading state while executing.
     */
    asyncAction?: (...args: any[]) => any
  }

  const props: CustomButtonProps = defineProps<CustomButtonProps>()
  const emit = defineEmits<{
    click: [event: MouseEvent]
  }>()

  const propsButton = omit<CustomButtonProps>(props, ['icon', 'asyncAction'])
  const slots = useSlots()
  const isLoading = ref(false)

  const icon = computed(() => {
    if (props.icon == null) return undefined
    return ToIcon(props.icon)
  })

  const currentIcon = computed(() => {
    if (isLoading.value || props.loading) {
      return SpinnerDefault
    }
    return icon.value
  })

  const iconClass = computed<CssClassType>(() => {
    return {
      'mx-0': true,

      'mr-1': (props.iconPos == 'left' || props.iconPos == null) && props.label != null,
      'ml-2 order-1': props.iconPos == 'right' && props.label != null,
      'mb-2': props.iconPos == 'top' && props.label != null,
      'mt-2': props.iconPos == 'bottom' && props.label != null,

      'size-4': (props.size as string) === 'extra-small',
      'size-5': props.size == 'small',
      'size-6': !props.size,
      'size-7': props.size == 'large'
    }
  })

  const propsClass = computed<CssClassType>(() => {
    const baseClass = { 'p-button-icon-only': !slots.default && !props.label }
    if (!props.class) return baseClass
    if (typeof props.class == 'string') return [props.class, baseClass]
    return { ...baseClass, ...props.class }
  })

  const isDisabled = computed(() => {
    return props.disabled || isLoading.value || props.loading
  })

  const handleClick = async (event: MouseEvent) => {
    if (isLoading.value || props.loading) return

    emit('click', event)

    if (props.asyncAction) {
      isLoading.value = true
      try {
        await props.asyncAction()
      } finally {
        isLoading.value = false
      }
    }
  }
</script>

<template>
  <Button
    :class="propsClass"
    v-bind="<ButtonProps>propsButton"
    v-bind:disabled="isDisabled"
    @click="handleClick">
    <component
      :is="currentIcon"
      v-if="currentIcon && (iconPos == 'left' || !iconPos)"
      :class="[props.iconClass ?? [], iconClass]" />
    <span
      :class="{ 'flex flex-col items-center': iconPos == 'top' || iconPos == 'bottom' }"
      class="duration-200 font-semibold">
      <component
        :is="currentIcon"
        v-if="currentIcon && iconPos == 'top'"
        :class="[props.iconClass ?? [], iconClass]" />
      <slot>{{ label }}</slot>
      <component
        :is="currentIcon"
        v-if="currentIcon && iconPos == 'bottom'"
        :class="[props.iconClass ?? [], iconClass]" />
    </span>
    <component
      :is="currentIcon"
      v-if="currentIcon && iconPos == 'right'"
      :class="[props.iconClass ?? [], iconClass]" />
  </Button>
</template>
